'use client';

import { useState } from 'react';
import { Button, ShareLinks } from '@paction-2.0/ui';

import styles from './breederVerifierPage.module.scss';

type FormTextFieldProps = {
  label: string;
  placeholder?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
};

function FormTextField({
  label,
  placeholder,
  name,
  value,
  onChange,
  className
}: FormTextFieldProps) {
  return (
    <div className={`${styles.textField} ${className || ''}`}>
      <label className={styles.textFieldLabel}>{label}</label>
      <div className={styles.textFieldInputContainer}>
        <input
          className={styles.textFieldInput}
          type="text"
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

type FormTextAreaProps = {
  label: string;
  placeholder?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
};

function FormTextArea({
  label,
  placeholder,
  name,
  value,
  onChange,
  className
}: FormTextAreaProps) {
  return (
    <div className={`${styles.areaTextField} ${className || ''}`}>
      <label className={styles.areaTextFieldLabel}>{label}</label>
      <div className={styles.areaTextFieldInputContainer}>
        <textarea
          className={styles.areaTextFieldInput}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  );
}

export function BreederVerifierForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [breederContact, setBreederContact] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    // TODO: Implement API endpoint for breeder verification requests
    // For now, simulate a successful submission
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSubmitted(true);
    setIsLoading(false);
  };

  return (
    <section className={styles.form}>
      <h2 className={styles.formTitle}>
        How do you know if a breeder is reputable? Ask us anything
      </h2>
      <div className={styles.formContainer}>
        <div className={styles.formResults}>
          <div className={styles.formResultsContainer}>
            <div className={styles.formIconWrapper}>
              <img
                className={styles.formIcon}
                alt="breeder verifier"
                src="/icons/breeder-verifier-preview.svg"
              />
            </div>
          </div>
        </div>

        {isSubmitted ? (
          <div className={styles.successMessage}>
            <div className={styles.successMessageText}>
              <p>Your request has been submitted.</p>
              <p>
                Thanks for sharing your experience with us. Paction will get
                back to you within 2 business days.
              </p>
            </div>
          </div>
        ) : (
          <div className={styles.formInputs}>
            <div className={styles.formRow}>
              <FormTextField
                className={styles.formInput}
                label="Your Name"
                placeholder="Enter your name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <FormTextField
                className={styles.formInput}
                label="Your Email"
                placeholder="Enter your email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <FormTextField
                className={styles.formInput}
                label="Your Phone"
                placeholder="Enter your phone number"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
            <div className={styles.formRow}>
              <FormTextField
                className={styles.formInput}
                label="Breeder's website or contact"
                placeholder="Enter breeder's website or contact"
                name="breederContact"
                value={breederContact}
                onChange={(e) => setBreederContact(e.target.value)}
              />
            </div>
            <div className={styles.formRow}>
              <FormTextArea
                className={styles.formInput}
                label="Any questions or anything else to share?"
                placeholder="Ask us anything"
                name="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
            <div className={styles.formButtonContainer}>
              <Button
                className={styles.formButton}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className={styles.formShare}>
        <ShareLinks link="https://paction.ca/breeder-verifier" onlyCopy />
      </div>
    </section>
  );
}
